import type {
  CPSInformations,
  PatientHash,
  PatientVitaleInformation,
  ReadCpxCardResult,
  ReadVitaleCardResult,
  VitaleCardPatient
} from "parog-web/cnam/types";
import {
  CnamApiCpsCardBlocked,
  CnamApiCpsCardError,
  CnamApiCpsCardWrongCode,
  CnamApiVitaleCardError
} from "parog-web/utils/cnam-api-errors";

import dayjs from "dayjs";
import type { CardReaderAdapter } from "parog-web/cnam-adapters/card-reader-adapter";
import ControlTower from "wise-control-tower";
import type DmpConnect from "./dmp-connect";
import type PinCodeManager from "./pin-code-manager";

export default class DmpConnectJSCardReaderAdapter
  implements CardReaderAdapter
{
  declare dmpConnect: DmpConnect;
  declare pinCodeManager: PinCodeManager;

  constructor(pinCodeManager: PinCodeManager, dmpConnect: DmpConnect) {
    this.pinCodeManager = pinCodeManager;
    this.dmpConnect = dmpConnect;
  }

  async _readCPSCard(): Promise<ReadCpxCardResult> {
    try {
      return await this.dmpConnect.readCPSCard();
    } catch (error) {
      if (!this.isCardError(error)) {
        ControlTower.captureMessage(
          `CnamApiError: ${error.message}. ${JSON.stringify(error.error)}`
        );
      } else {
        this.pinCodeManager.resetPinCode();
      }

      // Errors must be handled by the caller
      throw error;
    }
  }

  async _readVitaleCard(): Promise<ReadVitaleCardResult> {
    try {
      return await this.dmpConnect.readVitaleCard();
    } catch (error) {
      await this.dmpConnect.closeSession();

      if (!this.isCardError(error)) {
        ControlTower.captureMessage(
          `CnamApiError: ${error.message}. ${JSON.stringify(error.error)}`
        );
      }

      // Errors must be handled by the caller
      throw error;
    }
  }

  /**
   * Transforms a DmpConnectJS Patient to a hash compatible
   * with a Semeia Patient.
   */
  _vitalePatientToPatient(patient: VitaleCardPatient): PatientHash {
    const patientHash: PatientHash = {
      firstName: patient.s_given,
      lastName: patient.s_name,
      birthName: patient.s_birthname || patient.s_name,
      socialSecurityNumber: ""
    };

    if (patient.i_quality === 0) {
      patientHash.socialSecurityNumber = patient.s_nir.replace(" ", "");
    } else {
      patientHash.insuredSocialSecurityNumber = patient.s_nir.replace(" ", "");
      patientHash.birthrank = patient.i_birthRank;
    }

    // It seems the birthday is coded in different formats.
    if (patient.s_birthday.length === 8) {
      patientHash.birthday = dayjs(patient.s_birthday, "DDMMYYYY").toDate();
    } else if (patient.s_birthday.length === 6) {
      patientHash.birthday = dayjs(
        "19".concat(patient.s_birthday),
        "YYYYMMDD"
      ).toDate();
    }

    // Check that the date has been well parsed.
    // Otherwise, set it to null because an Invalid Date is not equals to another Invalid Date.
    // isNaN(new Date()) => false but isNaN(Invalid Date) => true
    if (isNaN(patientHash.birthday as unknown as number))
      patientHash.birthday = undefined;

    // Gender is not available as a property, but can be guessed from the NIR.
    switch (patientHash.socialSecurityNumber.slice(0, 1)) {
      case "1":
        patientHash.gender = "male";
        break;
      case "2":
        patientHash.gender = "female";
        break;
    }

    return patientHash;
  }

  async getCpsInformations(): Promise<CPSInformations> {
    const response = await this._readCPSCard();
    return {
      medicLastName: response.s_name,
      medicFirstName: response.s_given,
      medicInternalId: response.s_internalId,
      medicProfessionCode: response.s_profession,
      cpxSerialNumber: response.s_cpxSerialNumber,
      cpxValidityDate: dayjs(response.s_cpxValidityDate, "YYYYMMDD").toDate(),
      practiceLocations: response.PracticeLocations.map(practiceLocation => {
        return {
          activity: practiceLocation.s_practiceLocationActivity,
          structureName: practiceLocation.s_practiceLocationName,
          structureId: practiceLocation.s_practiceLocationStructureId,
          billingNumber: practiceLocation.s_practiceLocationBillingNumber
        };
      })
    };
  }

  async getVitaleInformations(): Promise<Array<PatientVitaleInformation>> {
    const result = await this._readVitaleCard();
    const patientHashes = result.Patients.map(patient => {
      return this._vitalePatientToPatient(patient);
    });

    return patientHashes.map(patientHash => {
      return {
        firstName: patientHash.firstName,
        birthName: patientHash.birthName || patientHash.firstName,
        lastName: patientHash.lastName,
        birthday: patientHash.birthday as Date,
        socialSecurityNumber: patientHash.socialSecurityNumber,
        insuredSocialSecurityNumber: patientHash.insuredSocialSecurityNumber,
        birthrank: patientHash.birthrank?.toString(),
        gender: patientHash.gender
      };
    });
  }

  isCardError(error) {
    return (
      error instanceof CnamApiVitaleCardError ||
      error instanceof CnamApiCpsCardError ||
      error instanceof CnamApiCpsCardWrongCode ||
      error instanceof CnamApiCpsCardBlocked
    );
  }
}
